import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SingleSelectDropdownComponent } from '../../../business-details/single-select-dropdown.component';
import { OffcanvasSheetComponent } from '../../../shared/offcanvas-sheet/offcanvas-sheet.component';

export interface BomComponentPayload {
  rawMaterialItemID: number | null;
  quantityRequired: number | null;
  componentUOMID: number | null;
  scrapFactor: number;
  notes: string;
}

export interface BomFormPayload {
  bomHeaderID?: number;
  finishedGoodItemID: number | null;
  description: string;
  isActive: boolean;
  components: BomComponentPayload[];
}

@Component({
  selector: 'app-bom-item-offcanvas',
  standalone: true,
  imports: [CommonModule, FormsModule, SingleSelectDropdownComponent, OffcanvasSheetComponent],
  templateUrl: './bom-item-offcanvas.component.html',
  styleUrls: [
    '../../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './bom-item-offcanvas.component.css',
  ],
})
export class BomItemOffcanvasComponent implements OnChanges {
  @Input() visible = false;
  @Input() bom: any = null;
  @Input() isEditing = false;
  @Input() products: any[] = [];
  @Input() rawMaterials: any[] = [];
  @Input() uoms: any[] = [];

  @Output() closeCanvas = new EventEmitter<void>();
  @Output() save = new EventEmitter<BomFormPayload>();

  readonly Math = Math;

  formData = {
    finishedGoodItemID: null as number | null,
    description: '',
    isActive: true,
  };

  @ViewChildren('lineCard') lineCards!: QueryList<ElementRef<HTMLElement>>;

  components: BomComponentPayload[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Invalidate memoized dropdown option arrays when their source inputs change.
    if (changes['products'] || changes['rawMaterials'] || changes['uoms']) {
      this.clearOptionCaches();
    }
    if (this.visible && (changes['visible'] || changes['bom'])) {
      if (this.bom) {
        this.formData = {
          finishedGoodItemID: this.bom.finishedGoodItemID ?? null,
          description: this.bom.description ?? '',
          isActive: this.bom.isActive ?? true,
        };
        this.components = (this.bom.components ?? []).map((c: any) => ({
          rawMaterialItemID: c.rawMaterialItemID ?? null,
          quantityRequired: c.quantityRequired ?? null,
          componentUOMID: c.componentUOMID ?? null,
          scrapFactor: c.scrapFactor ?? 0,
          notes: c.notes ?? '',
        }));
        if (this.components.length === 0) this.addComponent();
      } else {
        this.resetForm();
      }
    }
  }

  get title(): string {
    return this.isEditing ? 'Edit BOM' : 'Add BOM';
  }

  addComponent(): void {
    this.components.push({
      rawMaterialItemID: null,
      quantityRequired: null,
      componentUOMID: null,
      scrapFactor: 0,
      notes: '',
    });
    setTimeout(() => this.lineCards.last?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  }

  removeComponent(idx: number): void {
    this.components.splice(idx, 1);
  }

  onRawMaterialChange(comp: BomComponentPayload): void {
    const compatible = this.getUOMsForItem(comp.rawMaterialItemID);
    comp.componentUOMID = compatible.length > 0 ? compatible[0].uomid : null;
  }

  getUOMsForItem(itemId: number | null): any[] {
    if (!itemId) return [];
    const material = this.rawMaterials.find((rm: any) => rm.itemID === itemId);
    if (!material) return [];
    const uomId = material.baseUOMID ?? material.purchaseUOMID;
    if (uomId == null) return [];
    const baseUom = this.uoms.find((u: any) => u.uomid === uomId);
    if (!baseUom) return [];
    const baseFamily = this.getUOMFamily(baseUom);
    const compatible = this.uoms.filter((u: any) => this.getUOMFamily(u) === baseFamily);
    return [baseUom, ...compatible.filter((u: any) => u.uomid !== baseUom.uomid)];
  }

  private getUOMFamily(uom: any): string {
    const code = (uom.uomCode || '').toLowerCase();
    const name = (uom.uomName || '').toLowerCase();
    if (['kg', 'g', 'gram', 'kilogram', 'lb', 'pound', 'oz', 'ounce', 'mg'].some(k => code.includes(k) || name.includes(k))) return 'weight';
    const volumeExact = ['l', 'lt', 'ml'];
    const volumeTokens = ['liter', 'litre', 'milliliter', 'millilitre', 'gallon'];
    if (volumeExact.some(k => code === k) || volumeTokens.some(k => code.includes(k) || name.includes(k))) return 'volume';
    if (['m', 'meter', 'metre', 'cm', 'centimeter', 'mm', 'millimeter', 'inch', 'ft', 'foot'].some(k => code.includes(k) || name.includes(k))) return 'length';
    if (['ea', 'each', 'unit', 'pcs', 'piece', 'pc'].some(k => code.includes(k) || name.includes(k))) return 'count';
    return 'other';
  }

  // --- Dropdown option helpers ---
  //
  // These feed [options] on app-single-select-dropdown. They MUST return stable
  // array references across change-detection cycles: a fresh array each cycle
  // (combined with the dropdown's *ngFor) tore down/rebuilt every option node on
  // each CD while a panel was open — freezing touch on iOS. We memoize and clear
  // the caches when the source inputs change (see ngOnChanges). Defense-in-depth
  // alongside the dropdown's trackBy.

  private productOptionsCache: { value: string; label: string }[] | null = null;
  private rawMaterialOptionsCache: { value: string; label: string }[] | null = null;
  private uomOptionsCache = new Map<number, { value: string; label: string }[]>();

  private clearOptionCaches(): void {
    this.productOptionsCache = null;
    this.rawMaterialOptionsCache = null;
    this.uomOptionsCache.clear();
  }

  get productOptions(): { value: string; label: string }[] {
    if (!this.productOptionsCache) {
      this.productOptionsCache = this.products.map(p => ({ value: String(p.itemID), label: p.itemName }));
    }
    return this.productOptionsCache;
  }

  get rawMaterialOptions(): { value: string; label: string }[] {
    if (!this.rawMaterialOptionsCache) {
      this.rawMaterialOptionsCache = this.rawMaterials.map(rm => ({ value: String(rm.itemID), label: rm.itemName }));
    }
    return this.rawMaterialOptionsCache;
  }

  getUOMOptionsForItem(itemId: number | null): { value: string; label: string }[] {
    const key = itemId ?? -1;
    let cached = this.uomOptionsCache.get(key);
    if (!cached) {
      cached = this.getUOMsForItem(itemId).map(u => ({ value: String(u.uomid), label: u.uomCode || u.uomName }));
      this.uomOptionsCache.set(key, cached);
    }
    return cached;
  }

  onRawMaterialSelectChange(comp: BomComponentPayload, val: string | null): void {
    comp.rawMaterialItemID = val != null ? +val : null;
    this.onRawMaterialChange(comp);
  }

  // --- Save ---

  onSave(): void {
    const payload: BomFormPayload = {
      finishedGoodItemID: this.formData.finishedGoodItemID,
      description: this.formData.description,
      isActive: this.formData.isActive,
      components: this.components.map(c => ({ ...c })),
    };
    if (this.isEditing && this.bom?.bomHeaderID) {
      payload.bomHeaderID = this.bom.bomHeaderID;
    }
    this.save.emit(payload);
    this.onClose();
  }

  onClose(): void {
    this.closeCanvas.emit();
  }

  private resetForm(): void {
    this.formData = { finishedGoodItemID: null, description: '', isActive: true };
    this.components = [{ rawMaterialItemID: null, quantityRequired: null, componentUOMID: null, scrapFactor: 0, notes: '' }];
  }
}
